import React, {FC, ReactElement, useCallback, useEffect, useRef, useState} from 'react';

import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {Tooltip} from '@optimaxdev/design-system';

import {connect} from 'react-redux';

import {CART_BUTTON} from 'constants/analytics/events/cart';
import {removeCoupon} from 'desktop/pages/cart/store/reducers/coupons/coupons';
import {ApplicationStoreType} from 'desktop/store';
import {spawnPriorityTask} from 'libs/timers/spawnPriorityTask/spawnPriorityTask';

import s from './discountLabelV2.scss';

const CouponOverlay = ({coupon}: {coupon: string}) => <div className={s.overlay}>{coupon}</div>;

export type PropsType = {
    removeCouponFromCart: (coupon: string) => void;
    coupons: Array<string>;
};

/**
 * Displays all applied coupons and their discount
 */
export const DiscountLabelV2: FC<PropsType> = ({coupons, removeCouponFromCart}) => {
    const couponRefs = useRef<Array<HTMLParagraphElement | null>>([]);
    const [couponTooltips, setCouponTooltips] = useState<number[]>([]);

    /**
     * Remove coupon method
     * @param {string} coupon coupon code
     * @returns {VoidFunctionType} - On click handler
     */
    const remove = useCallback(
        (coupon: string): (() => void) =>
            () => {
                removeCouponFromCart(coupon);
                spawnPriorityTask(() => {
                    sendDesktopAnalyticsEvent(CART_BUTTON, {
                        action: 'Remove Coupon',
                        label: coupon,
                    });
                });
            },
        [removeCouponFromCart],
    );

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            let tooltipThreshold: number;

            if (width >= 1440) {
                tooltipThreshold = 220;
            } else if (width >= 1280) {
                tooltipThreshold = 200;
            } else {
                tooltipThreshold = 160;
            }

            const tooltipIndexes = couponRefs.current.reduce<number[]>((acc, coupon, index) => {
                if (coupon && coupon.clientWidth >= tooltipThreshold) {
                    acc.push(index);
                }
                return acc;
            }, []);

            setCouponTooltips(tooltipIndexes);
        };

        handleResize();

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [coupons]);

    const couponRedesign = (coupon: string, index: number) =>
        couponTooltips.includes(index) ? (
            <Tooltip
                closeTrigger="hover"
                content={<CouponOverlay coupon={coupon} />}
                openTrigger="hover"
                position="bottom"
                renderAs="div"
                size="small"
            >
                <p className={s.couponText}>
                    {coupon}
                    <>
                        <span className={s.couponStaticText}>&nbsp;Coupon</span>
                        <button
                            className={s.couponRemove}
                            data-test-name="couponRemove"
                            data-test-value={coupon}
                            onClick={remove(coupon)}
                            type="button"
                        >
                            Remove
                        </button>
                    </>
                </p>
            </Tooltip>
        ) : (
            <p className={s.couponText} ref={el => (couponRefs.current[index] = el)}>
                {coupon}
                <>
                    <span className={s.couponStaticText}>&nbsp;Coupon</span>
                    <button
                        className={s.couponRemove}
                        data-test-name="couponRemove"
                        data-test-value={coupon}
                        onClick={remove(coupon)}
                        type="button"
                    >
                        Remove
                    </button>
                </>
            </p>
        );

    return (
        <ul>
            {coupons.map(
                (coupon: string, index: number): ReactElement => (
                    <li key={coupon}>
                        <span
                            className={s.couponName}
                            data-test-name="appliedCoupon"
                            data-test-value={coupon}
                        >
                            {couponRedesign(coupon, index)}
                        </span>
                    </li>
                ),
            )}
        </ul>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType) => ({
    coupons: store.cart.totals.appliedCoupons,
});

export const DiscountLabelV2Connected = connect(mapStateToProps, {
    removeCouponFromCart: removeCoupon,
})(DiscountLabelV2);
